import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AdminDemoData } from '@platform/client';
import { CreateDemoUserRequestRole } from '@platform/client/schemas';
import { FormsInputDropdown, InlineFormInput } from '@platform/components';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';

type ApplicationStatusOption = 'not_submitted' | 'pending' | 'approved' | 'rejected' | 'waitlisted';

type NewDemoUserFormData = {
  role: CreateDemoUserRequestRole;
  cohortId?: number;
  organizationId?: number;
  applicationStatus?: ApplicationStatusOption;
};

export const CreateDemoUserModal = (props: { onClose: () => void }) => {
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const [successText, setSuccessText] = useState<string>();
  const { control, formState, register, handleSubmit, watch } = useForm<NewDemoUserFormData>({
    mode: 'onChange',
  });
  const role = watch('role');

  const createUserMutator = AdminDemoData.useCreateDemoUser({
    mutation: {
      onMutate: () => setModalStage('submitting'),
      onError: () => setModalStage('error'),
      onSuccess: (data) => {
        const applicationNote =
          data.applicationStatus ?
            ` — application ${data.applicationStatus}${data.cohortId ? `, cohort #${data.cohortId}` : ''}`
          : '';
        setSuccessText(`User #${data.userId} (${data.name}) created — ${data.email}${applicationNote}`);
        setModalStage('success');
      },
    },
  });

  const onSubmit: SubmitHandler<NewDemoUserFormData> = (data) => {
    createUserMutator.mutate({
      data: {
        role: data.role,
        cohortId: data.role === 'professional' && data.cohortId ? data.cohortId : null,
        organizationId:
          data.role === 'representative' && data.organizationId ? data.organizationId : null,
        applicationStatus:
          data.role === 'professional' &&
          data.applicationStatus &&
          data.applicationStatus !== 'not_submitted' ?
            data.applicationStatus
          : null,
      },
    });
  };

  return (
    <AdminFormModal
      title='Create Demo User'
      onClose={props.onClose}
      modalStage={modalStage}
      successText={successText}
      submitCTA={{
        text: 'Create',
        callback: () => {
          handleSubmit(onSubmit)();
        },
      }}
    >
      <div className='space-y-2'>
        <FormsInputDropdown
          type='default'
          control={control}
          name='role'
          label='Role'
          placeholder='Select a role'
          rules={{ required: 'Role is required' }}
          items={[
            { text: 'Professional', textValue: 'professional' },
            { text: 'Representative', textValue: 'representative' },
            { text: 'Admin', textValue: 'admin' },
          ]}
          error={formState.errors.role}
        />
        {role === 'professional' && (
          <>
            <FormsInputDropdown
              type='default'
              control={control}
              name='applicationStatus'
              label='Application Status'
              placeholder='Not submitted (show sign-up flow)'
              items={[
                { text: 'Not submitted (show sign-up flow)', textValue: 'not_submitted' },
                { text: 'Pending review', textValue: 'pending' },
                { text: 'Approved (dashboard/search access)', textValue: 'approved' },
                { text: 'Rejected', textValue: 'rejected' },
                { text: 'Waitlisted', textValue: 'waitlisted' },
              ]}
              error={undefined}
            />
            <InlineFormInput
              register={() => register('cohortId')}
              label='Cohort ID (optional)'
              placeholder='Leave blank for evergreen cohort if application submitted'
              errors={undefined}
            />
          </>
        )}
        {role === 'representative' && (
          <InlineFormInput
            register={() => register('organizationId')}
            label='Organization ID (optional)'
            placeholder='Leave blank to create unattached'
            errors={undefined}
          />
        )}
      </div>
    </AdminFormModal>
  );
};
